import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

export type CommandPaletteItem = {
  id: string
  label: string
  description?: string
  group: string
  icon?: React.ComponentType<React.ComponentProps<'svg'>>
}

/**
 * A remote group of results, fetched as the user types (unlike `items`, which is a small,
 * pre-loaded, synchronously-filtered list). Debounced, with a minimum query length and a
 * request-generation guard so a slow, stale response can never overwrite a newer one's results.
 */
export type AsyncCommandSource = {
  group: string
  minLength?: number
  search: (query: string) => Promise<CommandPaletteItem[]>
}

const ASYNC_DEBOUNCE_MS = 250

/**
 * A global search overlay, ported from
 * design/components/navigation/command-palettes/08-with-groups.jsx (with the icon-per-row
 * treatment from 05-with-icons.jsx) into a typed, reusable, data-agnostic component: the caller
 * supplies pre-loaded `items` (filtered client-side) and, optionally, an `asyncSource` for a
 * remote group (filtered server-side); it decides what a match does via `onSelect`.
 */
export function CommandPalette({
  open,
  onClose,
  items,
  onSelect,
  placeholder = 'Search…',
  prompt = 'Search',
  promptDescription,
  emptyLabel = "We couldn't find anything with that term.",
  asyncSource,
}: {
  open: boolean
  onClose: () => void
  items: CommandPaletteItem[]
  onSelect: (item: CommandPaletteItem) => void
  placeholder?: string
  prompt?: string
  promptDescription?: string
  emptyLabel?: string
  asyncSource?: AsyncCommandSource
}) {
  const [query, setQuery] = useState('')
  const [asyncResults, setAsyncResults] = useState<CommandPaletteItem[]>([])
  const [asyncLoading, setAsyncLoading] = useState(false)
  const requestId = useRef(0)
  const minLength = asyncSource?.minLength ?? 2

  useEffect(() => {
    if (!asyncSource) return
    const trimmed = query.trim()
    if (trimmed.length < minLength) {
      requestId.current++
      setAsyncResults([])
      setAsyncLoading(false)
      return
    }
    const id = ++requestId.current
    setAsyncLoading(true)
    const timer = setTimeout(() => {
      asyncSource
        .search(trimmed)
        .then((results) => {
          if (id === requestId.current) setAsyncResults(results)
        })
        .catch(() => {
          if (id === requestId.current) setAsyncResults([])
        })
        .finally(() => {
          if (id === requestId.current) setAsyncLoading(false)
        })
    }, ASYNC_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query, asyncSource, minLength])

  const localFiltered = query === '' ? [] : items.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
  const filtered = [...localFiltered, ...asyncResults]

  const groups = filtered.reduce<Record<string, CommandPaletteItem[]>>((acc, item) => {
    ;(acc[item.group] ??= []).push(item)
    return acc
  }, {})

  function close() {
    onClose()
    setQuery('')
    setAsyncResults([])
  }

  return (
    <Dialog transition className="relative z-50" open={open} onClose={close}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-zinc-950/25 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-zinc-950/50"
      />

      <div className="fixed inset-0 z-50 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-xl transform overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-zinc-950/10 transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-zinc-900 dark:ring-white/10"
        >
          <Combobox<CommandPaletteItem>
            onChange={(item) => {
              if (item) {
                onSelect(item)
                close()
              }
            }}
          >
            <div className="grid grid-cols-1">
              <ComboboxInput
                autoFocus
                className="col-start-1 row-start-1 h-12 w-full pr-4 pl-11 text-base text-zinc-950 outline-hidden placeholder:text-zinc-500 sm:text-sm dark:bg-zinc-900 dark:text-white dark:placeholder:text-zinc-500"
                placeholder={placeholder}
                onChange={(event) => setQuery(event.target.value)}
                onBlur={() => setQuery('')}
              />
              <MagnifyingGlassIcon
                className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-zinc-500"
                aria-hidden="true"
              />
            </div>

            {asyncLoading && (
              <div className="border-t border-zinc-950/5 px-4 py-1.5 text-xs text-zinc-400 dark:border-white/10 dark:text-zinc-500">Searching…</div>
            )}

            {query === '' && (
              <div className="border-t border-zinc-950/5 px-6 py-14 text-center text-sm sm:px-14 dark:border-white/10">
                <MagnifyingGlassIcon className="mx-auto size-6 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
                <p className="mt-4 font-semibold text-zinc-950 dark:text-white">{prompt}</p>
                {promptDescription && <p className="mt-2 text-zinc-500 dark:text-zinc-400">{promptDescription}</p>}
              </div>
            )}

            {filtered.length > 0 && (
              <ComboboxOptions static as="ul" className="max-h-80 scroll-pt-11 scroll-pb-2 space-y-2 overflow-y-auto pb-2">
                {Object.entries(groups).map(([group, groupItems]) => (
                  <li key={group}>
                    <h2 className="bg-zinc-100 px-4 py-2.5 text-xs font-semibold text-zinc-950 dark:bg-white/5 dark:text-white">{group}</h2>
                    <ul className="mt-2 text-sm text-zinc-800 dark:text-zinc-300">
                      {groupItems.map((item) => (
                        <ComboboxOption
                          key={`${item.group}:${item.id}`}
                          value={item}
                          className="group flex cursor-default items-center gap-3 px-4 py-2 select-none data-focus:bg-zinc-900 data-focus:text-white data-focus:outline-hidden dark:data-focus:bg-white dark:data-focus:text-zinc-950"
                        >
                          {item.icon && (
                            <item.icon
                              className="size-5 flex-none text-zinc-400 group-data-focus:text-white dark:group-data-focus:text-zinc-950"
                              aria-hidden="true"
                            />
                          )}
                          <span className="flex-auto truncate">{item.label}</span>
                          {item.description && (
                            <span className="ml-3 flex-none truncate text-zinc-400 group-data-focus:text-zinc-300 dark:group-data-focus:text-zinc-600">
                              {item.description}
                            </span>
                          )}
                        </ComboboxOption>
                      ))}
                    </ul>
                  </li>
                ))}
              </ComboboxOptions>
            )}

            {query !== '' && filtered.length === 0 && !asyncLoading && (
              <div className="border-t border-zinc-950/5 px-6 py-14 text-center text-sm sm:px-14 dark:border-white/10">
                <p className="font-semibold text-zinc-950 dark:text-white">No results found</p>
                <p className="mt-2 text-zinc-500 dark:text-zinc-400">{emptyLabel}</p>
              </div>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
